import axios from 'axios';

export interface Invitation {
  id: number;
  email: string;
  status: 'pending' | 'accepted' | 'declined';
  expires_at: string;
  created_at: string;
  organization_id: number;
  invited_by_id: number;
  user_id?: number;
}

export interface InvitationResponse {
  message: string;
  invitation: Invitation;
  subdomain?: string;
}

export interface InviteInfo {
  email: string;
  organization_name: string;
  user_exists: boolean;
}

export interface PendingInvitation {
  id: number;
  token: string;
  email: string;
  organization_name: string;
  organization_id: number;
  expires_at: string;
  created_at: string;
}

export interface OrganizationUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export const invitationService = {
  sendInvitation: async (organizationId: number, email: string): Promise<InvitationResponse> => {
    const response = await axios.post(`/api/organizations/${organizationId}/invites`, { email });
    return response.data;
  },

  getOrganizationInvites: async (organizationId: number): Promise<Invitation[]> => {
    const response = await axios.get(`/api/organizations/${organizationId}/invites`);
    return response.data;
  },

  resendInvitation: async (organizationId: number, invitationId: number): Promise<void> => {
    await axios.post(`/api/organizations/${organizationId}/invites/${invitationId}/resend`);
  },

  reinviteInvitation: async (organizationId: number, invitationId: number): Promise<void> => {
    await axios.post(`/api/organizations/${organizationId}/invites/${invitationId}/reinvite`);
  },

  deleteInvitation: async (organizationId: number, invitationId: number): Promise<void> => {
    await axios.delete(`/api/organizations/${organizationId}/invites/${invitationId}`);
  },

  getOrganizationUsers: async (organizationId: number): Promise<OrganizationUser[]> => {
    const response = await axios.get(`/api/organizations/${organizationId}/users`);
    return response.data;
  },

  removeUserFromOrganization: async (organizationId: number, userId: number): Promise<void> => {
    await axios.delete(`/api/organizations/${organizationId}/users/${userId}`);
  },

  acceptInvitation: async (token: string): Promise<InvitationResponse> => {
    const response = await axios.post(`/api/organizations/invites/${token}/accept`);
    return response.data;
  },

  declineInvitation: async (token: string): Promise<InvitationResponse> => {
    const response = await axios.post(`/api/organizations/invites/${token}/decline`);
    return response.data;
  },

  getInviteInfo: async (token: string): Promise<InviteInfo> => {
    const response = await axios.get(`/api/organizations/invite-info/${token}`);
    return response.data;
  },

  getMyPendingInvitations: async (): Promise<PendingInvitation[]> => {
    const response = await axios.get('/api/organizations/invites/pending/me');
    return response.data;
  },
};
